import React from "react";
import { Box, Typography, TextField, IconButton, Tooltip, Button, FormControl, Select, MenuItem, Checkbox } from "@mui/material";
import { Add, Delete } from "@mui/icons-material";
import styled from "@emotion/styled";
import { selectableSkills, skillOptionLabel } from "../../utils/skillUtils";

const Heading = styled(Typography)(({ theme }) => ({
    fontWeight: theme.typography.fontWeightMedium,
    marginBottom: theme.spacing(0.5),
}));

const HelperText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    marginBottom: theme.spacing(2),
}));

const EmptyText = styled(Typography)(({ theme }) => ({
    color: theme.palette.text.secondary,
    fontStyle: "italic",
    marginBottom: theme.spacing(2),
}));

const ItemRow = styled(Box)(({ theme }) => ({
    display: "flex",
    flexWrap: "wrap",
    alignItems: "flex-start",
    gap: theme.spacing(1),
    padding: theme.spacing(1.5),
    marginBottom: theme.spacing(1.5),
    border: `1px solid ${theme.palette.divider}`,
    borderRadius: theme.shape.borderRadius,
}));

const TitleField = styled(TextField)({
    flex: "1 1 220px",
});

const DescriptionField = styled(TextField)({
    flex: "2 1 320px",
});

const HoursField = styled(TextField)({
    width: 90,
});

const WorkTypeControl = styled(FormControl)({
    minWidth: 140,
});

const RowActions = styled(Box)(({ theme }) => ({
    display: "flex",
    alignItems: "center",
    gap: theme.spacing(0.5),
}));

const StepQuoteLineItems = ({ items, onChange, skills = [] }) => {
    const list = Array.isArray(items) ? items : [];

    const updateItem = (index, updates) => {
        onChange(list.map((item, i) => (i === index ? { ...item, ...updates } : item)));
    };

    const handleAdd = () => {
        onChange([...list, { id: crypto.randomUUID(), title: "", description: "", workType: "", hours: 0, isActionable: false }]);
    };

    const handleRemove = (index) => {
        onChange(list.filter((_, i) => i !== index));
    };

    return (
        <Box>
            <Heading>Line Items</Heading>
            <HelperText variant="body2">
                These are the standard tasks, hours and prices for this engagement. The AI reuses them when it drafts a quote from this template. The title is the short label the client sees; the description holds the full detail.
            </HelperText>

            {list.length === 0 ? (
                <EmptyText variant="body2">No line items yet. Add at least one — a quote template cannot be saved without one.</EmptyText>
            ) : (
                list.map((item, index) => (
                    <ItemRow key={item.id ?? index}>
                        <TitleField
                            size="small"
                            required
                            label="Title"
                            placeholder="Short title"
                            value={item.title || ""}
                            error={!(item.title || "").trim()}
                            helperText={!(item.title || "").trim() ? "Give this line a title, or remove the line." : " "}
                            onChange={(e) => updateItem(index, { title: e.target.value })}
                        />
                        <DescriptionField
                            size="small"
                            multiline
                            minRows={1}
                            label="Description"
                            placeholder="What this line covers"
                            value={item.description || ""}
                            onChange={(e) => updateItem(index, { description: e.target.value })}
                        />
                        <WorkTypeControl size="small">
                            <Select
                                value={item.workType || ""}
                                displayEmpty
                                onChange={(e) => updateItem(index, { workType: e.target.value || "" })}
                            >
                                <MenuItem value=""><em>Work type</em></MenuItem>
                                {selectableSkills(skills, item.workType).map((s) => (
                                    <MenuItem key={s._id} value={s._id}>{skillOptionLabel(s)}</MenuItem>
                                ))}
                            </Select>
                        </WorkTypeControl>
                        <HoursField
                            size="small"
                            type="number"
                            label="Hours"
                            inputProps={{ min: 0 }}
                            value={item.hours ?? 0}
                            onChange={(e) => updateItem(index, { hours: parseFloat(e.target.value) || 0 })}
                        />
                        <RowActions>
                            <Tooltip title="Creates a task on the project when the client accepts this quote">
                                <Checkbox
                                    size="small"
                                    checked={!!item.isActionable}
                                    onChange={(e) => updateItem(index, { isActionable: e.target.checked })}
                                />
                            </Tooltip>
                            <Tooltip title="Remove this line item">
                                <IconButton size="small" onClick={() => handleRemove(index)} aria-label="remove line item">
                                    <Delete fontSize="small" />
                                </IconButton>
                            </Tooltip>
                        </RowActions>
                    </ItemRow>
                ))
            )}

            <Button size="small" variant="outlined" startIcon={<Add />} onClick={handleAdd}>
                Add line item
            </Button>
        </Box>
    );
};

export default StepQuoteLineItems;
